import { getTheme } from "../../hooks/UseTheme"
import type { LabelBreakdownWithLabel } from "../../types/generated/api/LabelBreakdownWithLabel"
import type { NodePercentage } from "../../types/generated/api/NodePercentage"
import type { TopDataFeaturesProps } from "../Simulations"

/**
 * Format label breakdown data into a format that TopFeaturesGraph accepts.
 *
 * @param {LabelBreakdownWithLabel[]} labels - The label breakdown data
 * @returns {TopDataFeaturesProps[]} - The formatted data
 */
export const formatTopFeatures = (labels: LabelBreakdownWithLabel[]): TopDataFeaturesProps[] => {
  const features: TopDataFeaturesProps[] = []

  if (labels && labels.length > 0) {
    if (labels && labels.length > 0)
      for (const item of labels) {
        features.push({
          features: item.features,
          label: item.name,
        })
      }
  }

  // Return the formatted data
  return features
}

export const getDecisionFunnelData = (props: {
  node_percentages?: Array<NodePercentage>
  totalRequests?: number
}): {
  nodes: {
    id: string
    uuid: string
    color: string
    percentage: number
    nodeType: string
    name: string
  }[]
  links: {
    source: string
    target: string
    value: number
    percentage: number
    nodeType: string
    name: string
  }[]
} => {
  const { node_percentages, totalRequests } = props

  const data: {
    nodes: { id: string; uuid: string; color: string; percentage: number; nodeType: string; name: string }[]
    links: {
      source: string
      target: string
      value: number
      percentage: number
      nodeType: string
      name: string
    }[]
  } = {
    nodes: [],
    links: [],
  }

  if (!node_percentages || !totalRequests) return { nodes: [], links: [] }

  const GetNodeColor = (node: string): string => {
    const theme = getTheme()

    const Theme: Record<string, string> = {
      ruleset: theme.palette.pink.background[2],
      label: theme.palette.red.background[2],
      program: theme.palette.orange.background[2],
      scorecardset: theme.palette.yellow.background[2],
      filter: theme.palette.purple.background[2],
      calculator: theme.palette.orange.background[2],
      taglist: theme.palette.green.background[2],
      script: theme.palette.gray.background[2],
      project: theme.palette.blue.background[2],
    }

    return Theme[node] as string
  }

  const getNodeTitle = (item: NodePercentage): string => {
    switch (item.node_type) {
      case "calculator":
        return (item.equation as string) ?? (item.name as string)
      case "filter":
        return item.condition?.slice(2, -2) as string
      case "label":
        return item.return_label?.name as string
      case "project":
        return item.title.split(":")[1]?.split("#")[0]?.trim() ?? item.title
      default:
        return item.name as string
    }
  }

  node_percentages.forEach((item) => {
    data.nodes.push({
      id: item.uuid,
      uuid: item.uuid,
      nodeType: item.node_type,
      name: getNodeTitle(item),
      color: GetNodeColor(item.node_type),
      percentage: item.percentage,
    })
  })

  node_percentages.forEach((item) => {
    if (item.node_type === "filter") {
      const true_route = data.nodes.find((object) => object.uuid === item.true_route)
      true_route &&
        data.links.push({
          source: item.uuid,
          target: true_route.id,
          nodeType: true_route.nodeType,
          name: true_route.name,
          // TODO:: fix when backend return the number of requests per node
          value: Math.round((true_route.percentage * totalRequests) / 100),
          percentage: true_route.percentage,
        })

      const false_route = data.nodes.find((object) => object.uuid === item.false_route)
      false_route &&
        data.links.push({
          source: item.uuid,
          target: false_route.id,
          nodeType: false_route.nodeType,
          name: false_route.name,
          // TODO:: fix when backend return the number of requests per node
          value: Math.round((false_route.percentage * totalRequests) / 100),
          percentage: false_route.percentage,
        })
    } else if (item.resolution_route) {
      const resolution_route = data.nodes.find((object) => object.uuid === item.resolution_route)
      resolution_route &&
        data.links.push({
          source: item.uuid,
          target: resolution_route?.id ?? "",
          nodeType: resolution_route.nodeType,
          name: resolution_route.name,
          // TODO:: fix when backend return the number of requests per node
          value: Math.round(((resolution_route?.percentage ?? 0) * totalRequests) / 100),
          percentage: resolution_route?.percentage ?? 0,
        })
    } else if (item.uncovered_route) {
      const uncovered_route = data.nodes.find((object) => object.uuid === item.uncovered_route)

      uncovered_route &&
        data.links.push({
          source: item.uuid,
          target: uncovered_route.id ?? "",
          nodeType: uncovered_route.nodeType,
          name: uncovered_route.name,
          // TODO:: fix when backend return the number of requests per node
          value: Math.round(((uncovered_route?.percentage ?? 0) * totalRequests) / 100),
          percentage: uncovered_route?.percentage ?? 0,
        })
    }
  })

  return data
}
